import { Outlet, useLoaderData, useParams } from "@remix-run/react";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  createDocumentFromSection,
  getComponentFromContentfulRichTextEntry,
  getSections,
} from "~/contentful";
import * as routeUtils from "~/contentful/route-utils";
import { ComponentDataProvider } from "~/contexts/ComponentDataProvider";
import { Document as ContentfulDocument } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Fragment } from "react";
import { getBlackPage } from "~/components/utils/getBlackPage";
import { InsightsListingHero } from "~/components/heros";
import FloatingListPageNav from "~/components/Insight/FloatingListPageNav";
import { DataFunctionArgs, LoaderFunction } from "@remix-run/server-runtime";
import { Newsletter } from "~/components/shared";

export const headers = routeUtils.headers;

export const meta = routeUtils.meta;

export const loader: LoaderFunction = async (
  dataFunctionArgs: DataFunctionArgs,
) => {
  // include: 5 instead of the default 10 — the insights page body has grown large
  // enough to exceed Contentful's 7MB response limit at full depth. Depth 5 is
  // sufficient: page → module → module fields → linked entries → their fields.
  // insightsList data is fetched separately via getInsights() with a scoped select.
  const loaderData = routeUtils.getContentfulPageLoader("insights", undefined, {
    include: 5,
  })(dataFunctionArgs);

  return loaderData;
};

export default function InsightsPage() {
  const params = useParams();
  const { componentData, schemaOrg, breadcrumbSchema } =
    useLoaderData<routeUtils.ContentfulPageLoaderData>();

  // Nested route (e.g. /insights/:topicSlug or /insights/:topicSlug/:insightSlug): render only the child route.
  if (params.topicSlug) {
    return <Outlet />;
  }

  const { page } = componentData;

  const sections =
    page &&
    page.fields.body &&
    getSections(page.fields.body as ContentfulDocument);

  let insightsHero, newsletter;
  if (
    sections?.[0]?.nodes[0]?.data.target.sys.contentType.sys.id ===
    "insightsHero"
  ) {
    insightsHero = sections[0].nodes.shift()?.data.target;
  }
  if (
    sections?.length &&
    sections[0]?.nodes[sections[0].nodes.length - 1]?.data.target.sys
      .contentType.sys.id === "newsletter"
  ) {
    newsletter = sections[0].nodes.pop()?.data.target;
  }

  const combinedSchemas = breadcrumbSchema
    ? [schemaOrg, breadcrumbSchema]
    : [schemaOrg];

  return (
    <ComponentDataProvider componentData={componentData}>
      <SchemaOrg content={combinedSchemas} />
      <InsightsListingHero fields={insightsHero.fields} />
      <section className="relative">
        <FloatingListPageNav />

        {sections &&
          sections.map((section, index) => {
            const component = documentToReactComponents(
              createDocumentFromSection(section),
              getComponentFromContentfulRichTextEntry,
            );

            if (section.type === "inset") {
              return (
                <div key={index} className="global-container">
                  {component}
                </div>
              );
            } else {
              // Spacer for the nav
              return (
                <Fragment key={JSON.stringify(section.nodes)}>
                  {index === 0 && (
                    <section
                      className="h-mobile-nav lg:h-desktop-nav"
                      style={{
                        backgroundColor: getBlackPage(page.fields.slug)
                          ? "black"
                          : "white",
                      }}
                    />
                  )}
                  {component}
                </Fragment>
              );
            }
          })}
      </section>
      {newsletter?.fields && <Newsletter fields={newsletter.fields} />}
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
